// ==========================
// Version 1 - verifyUploadedNftAssets.js
// - Verifies uploaded NFT metadata and image are publicly retrievable before mint
// - Includes retry progress callbacks for the Immortalize wizard UI
// ==========================

const DEFAULT_ATTEMPTS = 6;
const DEFAULT_DELAY_MS = 3000;

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function resolveIpfsUrl(value, gatewayBase) {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) return null;
  if (!raw.startsWith('ipfs://')) return raw;

  const base = String(gatewayBase || 'https://gateway.pinata.cloud/ipfs/')
    .trim()
    .replace(/\/+$/, '');
  const cidPath = raw.replace(/^ipfs:\/\//, '').replace(/^ipfs\//, '').replace(/^\/+/, '');
  return `${base}/${cidPath}`;
}

function validateMetadata(metadata) {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    throw new Error('Metadata is not valid JSON.');
  }
  if (!metadata.name || typeof metadata.name !== 'string') {
    throw new Error('Metadata is missing a valid name.');
  }
  if (!metadata.description || typeof metadata.description !== 'string') {
    throw new Error('Metadata is missing a valid description.');
  }
  if (!metadata.image || typeof metadata.image !== 'string') {
    throw new Error('Metadata is missing a valid image URL.');
  }
}

async function fetchMetadata(metadataUrl) {
  const response = await fetch(metadataUrl, {
    method: 'GET',
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`Metadata returned HTTP ${response.status}.`);
  }

  const metadata = await response.json();
  validateMetadata(metadata);
  return metadata;
}

async function fetchImage(imageUrl) {
  const response = await fetch(imageUrl, {
    method: 'GET',
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`Image returned HTTP ${response.status}.`);
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType && !contentType.toLowerCase().startsWith('image/')) {
    throw new Error(`Image returned unexpected content type: ${contentType}.`);
  }

  const blob = await response.blob();
  if (!blob || blob.size <= 0) {
    throw new Error('Image response was empty.');
  }

  if (!contentType && blob.type && !blob.type.toLowerCase().startsWith('image/')) {
    throw new Error(`Image blob returned unexpected type: ${blob.type}.`);
  }

  return {
    contentType: contentType || blob.type || null,
    size: blob.size,
  };
}

export async function verifyUploadedNftAssets({
  metadataUrl,
  gatewayBase,
  attempts = DEFAULT_ATTEMPTS,
  delayMs = DEFAULT_DELAY_MS,
  onProgress,
} = {}) {
  const cleanMetadataUrl = typeof metadataUrl === 'string' ? metadataUrl.trim() : '';
  if (!cleanMetadataUrl) {
    throw new Error('Missing metadata URL for verification.');
  }

  let lastError = null;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      onProgress?.({
        attempt,
        attempts,
        stage: 'metadata',
        message: `Checking metadata availability (${attempt}/${attempts})...`,
      });

      const metadata = await fetchMetadata(cleanMetadataUrl);
      const imageUrl = resolveIpfsUrl(metadata.image, gatewayBase);
      if (!imageUrl) {
        throw new Error('Metadata image URL could not be resolved.');
      }

      onProgress?.({
        attempt,
        attempts,
        stage: 'image',
        message: `Checking image availability (${attempt}/${attempts})...`,
      });

      const image = await fetchImage(imageUrl);

      const result = {
        ok: true,
        attemptsUsed: attempt,
        metadataUrl: cleanMetadataUrl,
        imageUrl,
        metadata,
        image,
        verifiedAt: new Date().toISOString(),
      };

      onProgress?.({
        attempt,
        attempts,
        stage: 'done',
        message: 'Metadata and image are publicly available.',
        result,
      });

      return result;
    } catch (error) {
      lastError = error;
      onProgress?.({
        attempt,
        attempts,
        stage: 'retry',
        message:
          attempt < attempts
            ? `Not publicly ready yet. Retrying in ${Math.round(delayMs / 1000)} seconds...`
            : 'Public verification failed.',
        error,
      });

      if (attempt < attempts) {
        await delay(delayMs);
      }
    }
  }

  throw new Error(
    lastError?.message ||
      'Upload completed, but BWK could not verify public metadata and image availability.'
  );
}

// ==========================
// End Version 1 - verifyUploadedNftAssets.js
// ==========================
